import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  OnChanges,
  SimpleChanges,
  OnInit,
  ChangeDetectionStrategy,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterOrSortDropdownType, FilterSelectedEventType, SelectOptionType, TableTypes } from '../../models/types';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import {
  Observable,
  OperatorFunction,
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  tap
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-icam-select',
  templateUrl: './icam-select.component.html',
  styleUrl: './icam-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IcamSelectComponent implements OnChanges, OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  private _translateService = inject(TranslateService);

  options: SelectOptionType[] = [];
  private _entries: string[] = [];
  private _entryToIdMap = new Map<string, string>();

  name = '';
  translationPrefix: 'FILTER' | 'SORTING_CRITERIA' = 'FILTER';

  isLight = true;
  isDefaultSelected = true;
  isReloading = false;
  value?: string = '';
  selectedValue?: string = '';
  displayValue?: string = '';
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  @Input() type: TableTypes | null = null;
  @Input() field!: FilterOrSortDropdownType;
  @Input() selectType: 'filter' | 'sort' = 'filter';
  @Input() translateOptions = false;
  @Input() disabled = false;
  @ViewChild('instance', { static: false }) instance!: NgbTypeahead;

  @Output() selectedFilter = new EventEmitter<FilterSelectedEventType>();

  private _accountIdToNameMap = new Map<string, string>();
  private _accountNameToIdMap = new Map<string, string[]>();

  constructor() {
    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnInit() {
    this.name = this.field.key;
    this.options = this.field.values;
    this.isDefaultSelected = this.options.every((opt) => !opt.selected);
    this._entryToIdMap.clear();

    if (this.name === 'accountId') {
      this._entries = this.options.map((opt) => {
        const accountId = opt.id.toString();
        const accountName = opt.value;
        const displayText = `${accountName} (${accountId})`;
        this._accountIdToNameMap.set(accountId, accountName);
        if (!this._accountNameToIdMap.has(accountName)) {
          this._accountNameToIdMap.set(accountName, []);
        }
        this._accountNameToIdMap.get(accountName)?.push(accountId);
        this._entryToIdMap.set(displayText, opt.id.toString());
        return displayText;
      });
    } else if (
      this.name === 'customerCanUpload' ||
      this.name === 'associatedToMachine' ||
      this.name === 'uploadablePeriodically'
    ) {
      this._entries = this.field.values.map((v) => {
        const translationKey = v.id === 'true' ? 'COMMON.YES' : 'COMMON.NO';
        const translated = this._translateService.instant(translationKey);
        const displayValue = translated !== translationKey ? translated : (v.id === 'true' ? 'YES' : 'NO');
        this._entryToIdMap.set(displayValue, String(v.id));
        return displayValue;
      });
    } else if (this.name === 'status' || this.name === 'role' || this.name === 'active') {
      this._entries = this.field.values.map((v) => {
        const translationKey = this.translateOptions ? `FILTER.${v.value}` : v.value;
        const translated = this._translateService.instant(translationKey);
        const displayValue = translated !== translationKey ? translated : v.value;
        this._entryToIdMap.set(displayValue, String(v.id));
        return displayValue;
      });
    } else if (this.name === 'isFavorite') {
      this._entries = this.field.values.map((v) => {
        const translationKey = `FILTER.IS_FAVORITE.${v.id}`;
        const translated = this._translateService.instant(translationKey);
        const displayValue = translated !== translationKey ? translated : v.id;
        this._entryToIdMap.set(displayValue, String(v.id));
        return displayValue;
      });
    } else if (this.name === 'complexity') {
      this._entries = this.field.values.map((v) => {
        const translationKey = `COMMON.COMPLEXITY.${String(v.id).toUpperCase()}`;
        const translated = this._translateService.instant(translationKey);
        const displayValue = translated !== translationKey ? translated : v.id;
        this._entryToIdMap.set(displayValue, String(v.id));
        return displayValue;
      });
    } else if (this.name === 'operatingSector') {
      this._entries = this.field.values.map((v) => {
        this._entryToIdMap.set(v.value, String(v.id));
        return v.value;
      });
    } else if (this.name === 'orderBy' || this.name === 'date_period') {
      this._entries = this.field.values.map((v) => {
        const translationKey = this.translateOptions ? `SORTING_CRITERIA.${v.value}` : v.value;
        const translated = this._translateService.instant(translationKey);
        const displayValue = translated !== translationKey ? translated : v.value;
        this._entryToIdMap.set(displayValue, String(v.id));
        return displayValue;
      });
    } else {
      this._entries = this.field.values.map((v) => {
        const translationKey = this.translateOptions ? `FILTER.${v.value}` : v.value;
        const translated = this._translateService.instant(translationKey);
        const displayValue = translated !== translationKey ? translated : v.value;
        this._entryToIdMap.set(displayValue, String(v.id));
        return displayValue;
      });
    }

    const selectedOption = this.options.find((opt) => opt.selected);
    if (selectedOption) {
      this.selectedValue = String(selectedOption.id);
      this.value = this.getDisplayValue(String(selectedOption.id));
    }

    this.translationPrefix = this.selectType === 'filter' ? 'FILTER' : 'SORTING_CRITERIA';

    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['field'] && !changes['field'].firstChange) {
      const currentField = changes['field'].currentValue;

      if (currentField?.values) {
        this.options = currentField.values;
        this.isDefaultSelected = this.options.every((opt) => !opt.selected);

        // Ricostruisce _entries e _entryToIdMap con le nuove opzioni
        this._entryToIdMap.clear();
        this._entries = this.options.map((v) => {
          const translationKey = this.translateOptions ? `FILTER.${v.value}` : v.value;
          const translated = this._translateService.instant(translationKey);
          const displayValue = translated !== translationKey ? translated : v.value;
          this._entryToIdMap.set(displayValue, String(v.id));
          return displayValue;
        });

        // Resetta la selezione visiva se il valore corrente non è più tra le opzioni
        const currentStillValid = this.selectedValue
          ? this.options.some((opt) => String(opt.id) === this.selectedValue)
          : false;

        if (!currentStillValid) {
          this.value = '';
          this.selectedValue = '';
          this.displayValue = '';
          this.isDefaultSelected = true;
        }

        // Le nuove opzioni sono arrivate: riabilita il componente
        this.isReloading = false;

        this._cdr.markForCheck();
      }
    }
  }

  filterSelected(event: Event, name: string) {
    if (this.disabled) return;
    if (event.target) {
      const element = event.target as HTMLSelectElement;
      const selectedIndex = element.selectedIndex;
      const selectedOption = element.options[selectedIndex];

      const selectedValue = selectedOption.value;
      const text = selectedOption.text;

      this.selectedFilter.emit({
        name,
        selectedValue,
        operation: this.selectType,
        text
      });
    }
  }

  accountFilterSelected(event: NgbTypeaheadSelectItemEvent) {
    if (this.disabled) return;
    if (event && event.item) {
      const displayText = event.item;
      const matches = displayText.match(/^(.*) \(([^)]+)\)$/);

      if (matches && matches.length === 3) {
        const accountName = matches[1];
        const accountId = matches[2];

        this.selectedFilter.emit({
          name: 'accountId',
          selectedValue: accountId,
          operation: this.selectType,
          text: accountName,
          metadata: {
            accountId: accountId
          }
        });
      } else {
        console.warn('Account selection format unexpected:', displayText);
        this.selectedFilter.emit({
          name: 'accountId',
          selectedValue: displayText,
          operation: this.selectType,
          text: displayText
        });
      }
    }
  }

  search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) => {
        const filtered =
          term === ''
            ? this._entries
            : this._entries.filter((v) => typeof v === 'string' && v.toLowerCase().includes(term.toLowerCase()));
        return filtered.slice(0, 100);
      })
    );
  };

  itemSelected(selection: NgbTypeaheadSelectItemEvent, nameOverride?: string): void {
    if (this.disabled) return;
    const name = nameOverride || this.name;
    const selectionItemStr = String(selection.item);
    const selectedId = this._entryToIdMap.get(selectionItemStr) || selectionItemStr;
    const displayValue = this.getDisplayValue(selectedId);

    this.selectedValue = selectedId;
    this.value = displayValue;

    const text = displayValue;

    if (name === 'customerCanUpload' || name === 'associatedToMachine' || name === 'uploadablePeriodically') {
      this.selectedFilter.emit({
        name,
        selectedValue: selectedId,
        operation: this.selectType,
        text
      });
    } else {
      this.selectedFilter.emit({
        name,
        selectedValue: selectedId,
        operation: this.selectType,
        text
      });
    }
  }

  private getDisplayValue(id: string | number): string {
    const idStr = String(id);
    const option = this.options.find((opt) => String(opt.id) === idStr);
    if (!option) return String(id);

    let translationKey: string;

    if (this.name === 'accountId') {
      const accountId = option.id.toString();
      const accountName = option.value;
      return `${accountName} (${accountId})`;
    } else if (
      this.name === 'customerCanUpload' ||
      this.name === 'associatedToMachine' ||
      this.name === 'uploadablePeriodically'
    ) {
      translationKey = option.id === 'true' ? 'COMMON.YES' : 'COMMON.NO';
    } else if (this.name === 'status' || this.name === 'role' || this.name === 'active') {
      translationKey = this.translateOptions ? `FILTER.${option.value}` : option.value;
    } else if (this.name === 'isFavorite') {
      translationKey = `FILTER.IS_FAVORITE.${option.id}`;
    } else if (this.name === 'complexity') {
      translationKey = `COMMON.COMPLEXITY.${String(option.id).toUpperCase()}`;
    } else if (this.name === 'operatingSector') {
      translationKey = option.value;
    } else if (this.name === 'orderBy' || this.name === 'date_period') {
      translationKey = this.translateOptions ? `SORTING_CRITERIA.${option.value}` : option.value;
    } else {
      translationKey = option.value;
    }

    const translated = this._translateService.instant(translationKey);
    return translated !== translationKey ? translated : option.value;
  }

  private getTranslationKey(id: string): string {
    const option = this.options.find((opt) => opt.id === id);
    if (!option) return id;

    if (this.name === 'accountId') {
      return option.value;
    } else if (
      this.name === 'customerCanUpload' ||
      this.name === 'associatedToMachine' ||
      this.name === 'uploadablePeriodically'
    ) {
      return option.id === 'true' ? 'COMMON.YES' : 'COMMON.NO';
    } else if (this.name === 'status' || this.name === 'role' || this.name === 'active') {
      return this.translateOptions ? `FILTER.${option.value}` : option.value;
    } else if (this.name === 'isFavorite') {
      return `FILTER.IS_FAVORITE.${option.id}`;
    } else if (this.name === 'complexity') {
      return `COMMON.COMPLEXITY.${String(option.id).toUpperCase()}`;
    } else if (this.name === 'operatingSector') {
      return option.value;
    } else if (this.name === 'orderBy' || this.name === 'date_period') {
      return this.translateOptions ? `SORTING_CRITERIA.${option.value}` : option.value;
    }
    return option.value;
  }

  public setSelectedValue(value: string): void {
    if (!value) return;

    const option = this.options.find((opt) => opt.id === value);
    if (option) {
      this.options.forEach((opt) => (opt.selected = false));

      option.selected = true;

      this.selectedValue = value;
      this.value = this.getDisplayValue(value);

      this.isDefaultSelected = false;
    }
  }

  clearSelection(): void {
    if (this.disabled) return;
    this.value = '';
    this.selectedValue = '';
    this.displayValue = '';

    this.options.forEach((opt) => (opt.selected = false));
    this.isDefaultSelected = true;

    // Disabilita temporaneamente il componente finché non arrivano le nuove opzioni
    this.isReloading = true;
    this._cdr.markForCheck();

    this.selectedFilter.emit({
      name: this.name,
      selectedValue: '',
      operation: this.selectType,
      text: ''
    });
  }
}
